// The trade engine (TRADES.md "The trade lifecycle"): a book of trades in,
// an intent and the time in, the new book and events out. No clocks, no
// I/O, no sport names. The host (Durable Object, simulator, client) applies
// the roster moves an `executed` event carries and decides when to call
// `advance` with the current time.

import {
  rosterCapacity,
  rosterProblem,
  seatsShort,
  starterShortfall,
  unseatable,
} from '@fsp/domain';
import type {
  Applied,
  Trade,
  TradeBook,
  TradeContext,
  TradeEvent,
  TradeIntent,
  TradeMove,
  TradeSide,
} from './types';

const H = 3600 * 1000;

/** A sentence's first letter capitalized: a pick's name opens one. */
function upper(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function reject(book: TradeBook, code: string, message: string): Applied {
  return { book, events: [{ type: 'rejected', code, message }] };
}

function replace(book: TradeBook, trade: Trade): TradeBook {
  return { trades: book.trades.map((t) => (t.id === trade.id ? trade : t)) };
}

function side(trade: Trade, teamId: string): TradeSide | null {
  return trade.sides.find((s) => s.teamId === teamId) ?? null;
}

function other(trade: Trade, teamId: string): TradeSide {
  return trade.sides.find((s) => s.teamId !== teamId)!;
}

export function isOpen(trade: Trade): boolean {
  return trade.status === 'proposed' || trade.status === 'review' || trade.status === 'accepted';
}

/**
 * The players a team's own open trades name — either side's, and the drops
 * that make room — but `except`'s. What a bot holds to: dropped, offered or
 * agreed to again, one of them would void a trade it is in (B-293). ONLY ITS
 * OWN TRADES: another manager's offer is private to its two sides (B-184), and
 * a bot knows what a manager in its seat knows (B-48).
 */
export function spokenFor(
  trades: readonly Trade[],
  teamId: string,
  except: string | null = null,
): Set<string> {
  const out = new Set<string>();
  for (const t of trades) {
    if (t.id === except || !isOpen(t) || !t.sides.some((s) => s.teamId === teamId)) continue;
    for (const s of t.sides) for (const id of [...s.playerIds, ...s.dropPlayerIds]) out.add(id);
  }
  return out;
}

/** Whether the deadline has passed: no new offers after the deadline period ends. */
export function pastDeadline(ctx: TradeContext): boolean {
  return ctx.rules.deadlinePeriod !== null && ctx.currentPeriod > ctx.rules.deadlinePeriod;
}

/** Why trading is closed for everyone right now, or null. */
export function closedReason(ctx: TradeContext): string | null {
  if (pastDeadline(ctx))
    return `The trade deadline was the end of Week ${ctx.rules.deadlinePeriod}.`;
  if (ctx.inPlayoffs && !ctx.rules.allowDuringPlayoffs) return 'No trades during the playoffs.';
  return null;
}

/** How many vetoes a league vote needs, given who may vote. */
export function vetoesNeeded(ctx: TradeContext, trade: Trade): number {
  const voters = ctx.teamIds.filter((t) => !side(trade, t)).length;
  const t = ctx.rules.vetoThreshold;
  if (!t) return Math.max(1, Math.ceil(voters / 3));
  if (t.kind === 'count') return Math.max(1, Math.min(voters, Math.round(t.value)));
  return Math.max(1, Math.ceil(voters * t.value));
}

/** The roster a side would hold after the trade, given what it receives. */
function rosterAfter(ctx: TradeContext, s: TradeSide, incoming: readonly string[]): string[] {
  const roster = ctx.rosters[s.teamId] ?? [];
  return [
    ...roster.filter((id) => !s.playerIds.includes(id) && !s.dropPlayerIds.includes(id)),
    ...incoming,
  ];
}

/**
 * A roster less the team's players on IR (D9) and its taxi squad, and those a
 * trade lands on its taxi squad (§2.7): what every limit counts.
 */
function activeOf(ctx: TradeContext, teamId: string, roster: readonly string[]): string[] {
  const held = ctx.reserved?.(teamId);
  const lands = ctx.lands?.(teamId, roster);
  if ((held === undefined || held.size === 0) && (lands === undefined || lands.size === 0))
    return [...roster];
  return roster.filter((id) => !(held?.has(id) ?? false) && !(lands?.has(id) ?? false));
}

/**
 * Why an active roster would be illegal: its size and its positions' limits
 * (`rosterProblem`), then whether everybody on it has a seat — a full bench
 * with a starting slot nobody left can play is a player in no row of the
 * week (`unseatable`, the trade review of 2026-10-22, B-144's rule for a
 * trade).
 */
function legalProblem(ctx: TradeContext, active: readonly string[]): string | null {
  const counted = rosterProblem(active, ctx.roster, ctx.positionOf);
  if (counted) return counted;
  const open = unseatable(active, ctx.roster, ctx.positionOf);
  return open === null ? null : `Nobody left can play ${open}, and the bench is full.`;
}

/**
 * A player the trade moves who is no longer where it says — either side's,
 * or a drop already gone — with his team, or null. Asked at accept and at
 * execution: a trade whose player has gone is void, as Yahoo voids it (the
 * trade review of 2026-10-22, TRADES.md step 3). So is one whose draft pick
 * the side no longer holds, or that can no longer be traded: traded since, or
 * its draft begun (PLAN-2026-10-27-the-next-season §2.6 rule 2).
 */
function goneFrom(
  ctx: TradeContext,
  trade: Trade,
): { id: string; teamId: string; pick: boolean } | null {
  for (const s of trade.sides) {
    const roster = ctx.rosters[s.teamId] ?? [];
    const id = [...s.playerIds, ...s.dropPlayerIds].find((p) => !roster.includes(p));
    if (id !== undefined) return { id, teamId: s.teamId, pick: false };
    const held = ctx.picks?.[s.teamId] ?? [];
    const pick = s.pickIds.find((p) => !held.includes(p));
    if (pick !== undefined) return { id: pick, teamId: s.teamId, pick: true };
  }
  return null;
}

/** A pick's name, or the host's word for one it cannot name. */
function pickNameOf(ctx: Pick<TradeContext, 'pickName'>, id: string): string {
  return ctx.pickName?.(id) ?? 'a draft pick';
}

/** A trade made void because one of its players, or picks, has gone. */
function voidGone(
  book: TradeBook,
  trade: Trade,
  gone: { id: string; teamId: string; pick: boolean },
  now: number,
  ctx: TradeContext,
): Applied {
  const voided = resolve(
    trade,
    'void',
    'clock',
    gone.pick
      ? `${upper(pickNameOf(ctx, gone.id))} is no longer ${ctx.teamName(gone.teamId)}’s to trade.`
      : `${ctx.playerName(gone.id)} is no longer on ${ctx.teamName(gone.teamId)}.`,
    now,
  );
  return {
    book: replace(book, voided),
    events: [{ type: 'void', trade: voided, reason: voided.resolution!.reason! }],
  };
}

/**
 * Whether a team's roster, before any drop, is over its size or a position's
 * limit: what a drop can put right, and so when its drop picker is offered
 * (TRADES.md step 3). IR counts toward neither (D9). Asked of the roster
 * BEFORE the drops already picked, so the picker stays while they are picked.
 */
export function overLimits(ctx: TradeContext, teamId: string, roster: readonly string[]): boolean {
  if (!ctx.rules.requireRosterLegalAfter) return false;
  const active = activeOf(ctx, teamId, roster);
  if (active.length > rosterCapacity(ctx.roster)) return true;
  // A PLAYER WITH NO SEAT is put right by a drop too: it frees a bench row.
  // Not where the league allows no empty slot and the lineup is short a
  // position, which no drop fills: a picker there could never succeed.
  if (
    unseatable(active, ctx.roster, ctx.positionOf) !== null &&
    (ctx.roster.allowEmptySlots || starterShortfall(active, ctx.roster, ctx.positionOf) === null)
  )
    return true;
  const byPos = new Map<string, number>();
  for (const id of active) {
    const pos = ctx.positionOf(id);
    byPos.set(pos, (byPos.get(pos) ?? 0) + 1);
  }
  return Object.entries(ctx.roster.maxPerPosition).some(
    ([pos, max]) => (byPos.get(pos) ?? 0) > max,
  );
}

/**
 * The fewest of `candidates` — cheapest first, as the host ranks them —
 * whose drop leaves a team's roster after a trade legal: within its size and
 * its positions' limits, and everybody seated (`legalProblem`). What a bot
 * drops on taking a trade. By its lowest worths alone it dropped its only
 * kicker and left the player coming in no seat, and the engine refused its
 * answer (the trade review's chunk 5). Empty when none is needed; the
 * cheapest it found when no choice is legal, which the engine then refuses.
 */
export function dropsToFit(
  ctx: TradeContext,
  teamId: string,
  roster: readonly string[],
  candidates: readonly string[],
): string[] {
  const legal = (r: readonly string[]) => legalProblem(ctx, activeOf(ctx, teamId, r)) === null;
  if (!ctx.rules.requireRosterLegalAfter || legal(roster)) return [];
  // HOW FAR FROM LEGAL a roster is: players with no seat, past its size
  // included, and players past a position's limit. Each drop is the one that
  // brings it nearest, the cheapest of those level — so two receivers in for
  // a team's only kicker drop two, and never the kicker.
  const distance = (r: readonly string[]) => {
    const active = activeOf(ctx, teamId, r);
    const byPos = new Map<string, number>();
    for (const id of active)
      byPos.set(ctx.positionOf(id), (byPos.get(ctx.positionOf(id)) ?? 0) + 1);
    const pastLimits = Object.entries(ctx.roster.maxPerPosition).reduce(
      (n, [pos, max]) => n + Math.max(0, (byPos.get(pos) ?? 0) - max),
      0,
    );
    return seatsShort(active, ctx.roster, ctx.positionOf) + pastLimits;
  };
  const pool = candidates.filter((id) => roster.includes(id));
  const picks: string[] = [];
  let left = [...roster];
  while (!legal(left) && picks.length < pool.length) {
    let best: { id: string; d: number } | null = null;
    for (const id of pool) {
      if (picks.includes(id)) continue;
      const d = distance(left.filter((x) => x !== id));
      if (best === null || d < best.d) best = { id, d };
    }
    if (best === null) break;
    picks.push(best.id);
    left = left.filter((x) => x !== best!.id);
  }
  return picks;
}

/** Why a side's roster would be illegal after the trade, or null. IR counts toward nothing (D9). */
export function sideProblem(ctx: TradeContext, trade: Trade, teamId: string): string | null {
  if (!ctx.rules.requireRosterLegalAfter) return null;
  const s = side(trade, teamId)!;
  return legalProblem(
    ctx,
    activeOf(ctx, teamId, rosterAfter(ctx, s, other(trade, teamId).playerIds)),
  );
}

/**
 * Why `teamId` may not take this trade on: a player still on its IR who no
 * longer qualifies (D9). Asked when a manager proposes or accepts.
 */
export function holdProblem(ctx: TradeContext, trade: Trade, teamId: string): string | null {
  const s = side(trade, teamId);
  if (!s || !ctx.reserveHold) return null;
  return ctx.reserveHold(teamId, rosterAfter(ctx, s, other(trade, teamId).playerIds));
}

/**
 * Why a proposal would be refused, or null. Checked at propose, and again
 * at accept and execution so nothing lands on an illegal roster.
 */
export function proposalProblem(
  ctx: TradeContext,
  input: {
    by: string;
    to: string;
    give: readonly string[];
    get: readonly string[];
    /** Draft picks each way (§2.6). Absent: none. */
    givePicks?: readonly string[];
    getPicks?: readonly string[];
    myDrops: readonly string[];
  },
): string | null {
  const closed = closedReason(ctx);
  if (closed) return closed;
  if (input.by === input.to) return 'Pick another team to trade with.';
  if (!ctx.teamIds.includes(input.to)) return 'Unknown team.';
  const givePicks = input.givePicks ?? [];
  const getPicks = input.getPicks ?? [];
  if (
    input.give.length === 0 &&
    input.get.length === 0 &&
    givePicks.length === 0 &&
    getPicks.length === 0
  )
    return 'Pick at least one player or pick on either side.';
  // PLAYERS AND PICKS TOGETHER count toward the limit (§2.6 rule 3).
  const max = ctx.rules.maxPlayersPerSide;
  const sent = input.give.length + givePicks.length;
  const asked = input.get.length + getPicks.length;
  if (sent > max || asked > max)
    return `That's ${Math.max(sent, asked)}; the limit is ${max} per side.`;
  const mine = ctx.rosters[input.by] ?? [];
  const theirs = ctx.rosters[input.to] ?? [];
  const notMine = input.give.find((id) => !mine.includes(id));
  if (notMine) return `${ctx.playerName(notMine)} is not on your roster.`;
  const notTheirs = input.get.find((id) => !theirs.includes(id));
  if (notTheirs) return `${ctx.playerName(notTheirs)} is not on ${ctx.teamName(input.to)}.`;
  const badDrop = input.myDrops.find((id) => !mine.includes(id) || input.give.includes(id));
  if (badDrop) return `${ctx.playerName(badDrop)} cannot be dropped here.`;
  if (new Set([...input.give, ...input.get]).size !== input.give.length + input.get.length)
    return 'A player is listed twice.';
  if (givePicks.length > 0 || getPicks.length > 0) {
    if (!ctx.rules.assets.draftPicks || ctx.picks === undefined)
      return 'This league does not trade draft picks now.';
    const myPicks = ctx.picks[input.by] ?? [];
    const theirPicks = ctx.picks[input.to] ?? [];
    const notMinePick = givePicks.find((id) => !myPicks.includes(id));
    if (notMinePick) return `${upper(pickNameOf(ctx, notMinePick))} is not yours to trade.`;
    const notTheirsPick = getPicks.find((id) => !theirPicks.includes(id));
    if (notTheirsPick)
      return `${upper(pickNameOf(ctx, notTheirsPick))} is not ${ctx.teamName(input.to)}’s to trade.`;
    if (new Set([...givePicks, ...getPicks]).size !== givePicks.length + getPicks.length)
      return 'A pick is listed twice.';
    const heldAfter = (held: readonly string[], out: readonly string[], inc: readonly string[]) => [
      ...held.filter((id) => !out.includes(id)),
      ...inc,
    ];
    const mineKept = ctx.keeperProblem?.(input.by, heldAfter(myPicks, givePicks, getPicks)) ?? null;
    if (mineKept) return mineKept;
    const theirsKept =
      ctx.keeperProblem?.(input.to, heldAfter(theirPicks, getPicks, givePicks)) ?? null;
    if (theirsKept) return `${ctx.teamName(input.to)}: ${theirsKept}`;
  }
  const after = [
    ...mine.filter((id) => !input.give.includes(id) && !input.myDrops.includes(id)),
    ...input.get,
  ];
  const hold = ctx.reserveHold?.(input.by, after) ?? null;
  if (hold) return hold;
  if (ctx.rules.requireRosterLegalAfter) {
    const problem = legalProblem(ctx, activeOf(ctx, input.by, after));
    if (problem) return `Your roster after: ${problem}`;
  }
  return null;
}

function resolve(
  trade: Trade,
  status: Trade['status'],
  by: Trade['resolution'] extends infer R ? (R extends { by: infer B } ? B : never) : never,
  reason: string | null,
  at: number,
): Trade {
  return { ...trade, status, resolution: { by, reason, at } };
}

/** The roster moves an executed trade makes; the host applies them. */
export function movesFor(trade: Trade): TradeMove[] {
  const [a, b] = trade.sides;
  return [
    {
      teamId: a.teamId,
      added: [...b.playerIds],
      removed: [...a.playerIds, ...a.dropPlayerIds],
      dropped: [...a.dropPlayerIds],
      picksAdded: [...b.pickIds],
    },
    {
      teamId: b.teamId,
      added: [...a.playerIds],
      removed: [...b.playerIds, ...b.dropPlayerIds],
      dropped: [...b.dropPlayerIds],
      picksAdded: [...a.pickIds],
    },
  ];
}

/**
 * A player in the trade whose game has started, or null. A side's extra
 * DROPS count too: a dropped player whose game has started keeps his stamped
 * lineup cell until the period ends, so executing now would leave him starting
 * for a team he is not on (B-148, the rule the waiver path keeps by refusing
 * the drop). The trade waits for the week's end instead. So does one with a
 * player the host holds (`holds`): a bot's starter still to play once the
 * week has begun, whose slot it may have nobody left to fill.
 */
function anyLocked(ctx: TradeContext, trade: Trade): string | null {
  for (const s of trade.sides)
    for (const id of [...s.playerIds, ...s.dropPlayerIds])
      if (ctx.locked.has(id) || (ctx.holds?.has(id) ?? false)) return id;
  return null;
}

/** Clear a trade for execution: now, or at the period's end when a player is locked. */
/** Whether a bot plays either side of a trade now (B-481). */
export function withBot(ctx: TradeContext, trade: Trade): boolean {
  return trade.sides.some((s) => ctx.bots?.has(s.teamId) ?? false);
}

function clear(book: TradeBook, trade: Trade, now: number, ctx: TradeContext): Applied {
  const locked = anyLocked(ctx, trade);
  const executeAt = locked ? Math.max(now, ctx.periodEnd) : now;
  const cleared: Trade = { ...trade, status: 'accepted', executeAt };
  const next = replace(book, cleared);
  if (executeAt <= now) return execute(next, cleared, now, ctx);
  return { book: next, events: [{ type: 'accepted', trade: cleared }] };
}

function execute(book: TradeBook, trade: Trade, now: number, ctx: TradeContext): Applied {
  // Last look: both rosters still hold what they give, and both are legal after.
  const gone = goneFrom(ctx, trade);
  if (gone) return voidGone(book, trade, gone, now, ctx);
  for (const s of trade.sides) {
    const problem = sideProblem(ctx, trade, s.teamId);
    if (problem) {
      const voided = resolve(
        trade,
        'void',
        'clock',
        `${ctx.teamName(s.teamId)}'s roster would not be legal: ${problem}`,
        now,
      );
      return {
        book: replace(book, voided),
        events: [{ type: 'void', trade: voided, reason: voided.resolution!.reason! }],
      };
    }
  }
  // THE KEEPERS STILL FIT THE PICKS EACH SIDE WILL HOLD (§2.6 rule 6): a
  // team that changed its keepers while the trade waited on its review could
  // otherwise keep a player on a pick it no longer has, drafted for the team
  // that took it (the review of chunk 5).
  const kept = keeperProblemOf(ctx, trade);
  if (kept) {
    const voided = resolve(trade, 'void', 'clock', kept, now);
    return {
      book: replace(book, voided),
      events: [{ type: 'void', trade: voided, reason: voided.resolution!.reason! }],
    };
  }
  const done: Trade = {
    ...trade,
    status: 'executed',
    executeAt: now,
    resolution: trade.resolution ?? { by: 'accept', reason: null, at: now },
  };
  const events: TradeEvent[] = [{ type: 'executed', trade: done, moves: movesFor(done) }];
  let next = replace(book, done);
  // Any other open offer that touches these players, or these picks, is
  // void: they moved.
  const moved = new Set(done.sides.flatMap((s) => [...s.playerIds, ...s.dropPlayerIds]));
  const movedPicks = new Set(done.sides.flatMap((s) => s.pickIds));
  for (const t of next.trades) {
    if (t.id === done.id || !isOpen(t)) continue;
    const hit = t.sides.flatMap((s) => s.playerIds).find((id) => moved.has(id));
    const pickHit = t.sides.flatMap((s) => s.pickIds).find((id) => movedPicks.has(id));
    if (hit === undefined && pickHit === undefined) continue;
    const voided = resolve(
      t,
      'void',
      'clock',
      hit !== undefined
        ? `${ctx.playerName(hit)} was traded in another deal.`
        : `${upper(pickNameOf(ctx, pickHit!))} was traded in another deal.`,
      now,
    );
    next = replace(next, voided);
    events.push({ type: 'void', trade: voided, reason: voided.resolution!.reason! });
  }
  return { book: next, events };
}

/** Apply one intent at time `now`. Never throws; a bad intent yields a `rejected` event. */
export function apply(
  book: TradeBook,
  intent: TradeIntent,
  now: number,
  ctx: TradeContext,
): Applied {
  if (intent.type === 'propose') {
    const problem = proposalProblem(ctx, intent);
    if (problem) return reject(book, 'invalid', problem);
    const trade: Trade = {
      id: ctx.newId(now),
      proposerTeamId: intent.by,
      sides: [
        {
          teamId: intent.by,
          playerIds: [...intent.give],
          pickIds: [...(intent.givePicks ?? [])],
          faab: 0,
          dropPlayerIds: [...intent.myDrops],
        },
        {
          teamId: intent.to,
          playerIds: [...intent.get],
          pickIds: [...(intent.getPicks ?? [])],
          faab: 0,
          dropPlayerIds: [],
        },
      ],
      note: intent.note.trim().slice(0, 300),
      status: 'proposed',
      createdAt: now,
      respondBy: now + ctx.offerTtlMs,
      reviewEndsAt: null,
      executeAt: null,
      counterOf: intent.counterOf,
      counteredBy: null,
      votes: [],
      resolution: null,
    };
    let next: TradeBook = { trades: [...book.trades, trade] };
    const events: TradeEvent[] = [];
    // A counter closes the original; a new offer replaces my other open offer to the same team.
    for (const t of book.trades) {
      if (!isOpen(t) || t.status !== 'proposed') continue;
      const between = side(t, intent.by) && side(t, intent.to);
      if (!between) continue;
      if (t.id === intent.counterOf) {
        const countered: Trade = {
          ...t,
          status: 'countered',
          counteredBy: trade.id,
          resolution: { by: 'manager', reason: null, at: now },
        };
        next = replace(next, countered);
        events.push({ type: 'countered', trade, original: countered });
      } else if (t.proposerTeamId === intent.by) {
        const withdrawn = resolve(t, 'withdrawn', 'manager', 'Replaced by a new offer.', now);
        next = replace(next, withdrawn);
        events.push({ type: 'withdrawn', trade: withdrawn });
      }
    }
    if (!events.some((e) => e.type === 'countered')) events.unshift({ type: 'proposed', trade });
    return { book: next, events };
  }

  const trade = book.trades.find((t) => t.id === intent.tradeId);
  if (!trade) return reject(book, 'unknown', 'That trade is gone.');
  const isCommish = ctx.reviewerTeamIds.includes(intent.by);

  switch (intent.type) {
    case 'accept': {
      if (trade.status !== 'proposed')
        return reject(book, 'not-open', 'That offer is no longer open.');
      if (trade.proposerTeamId === intent.by || !side(trade, intent.by))
        return reject(book, 'not-yours', 'Only the manager who received the offer can accept it.');
      const closed = closedReason(ctx);
      if (closed) return reject(book, 'closed', closed);
      // A PLAYER IN IT HAS GONE: the offer can never go through, so it is
      // void now, with the reason, rather than accepted and voided later
      // with a green "Done." on the screen (the trade review's finding 11).
      const gone = goneFrom(ctx, trade);
      if (gone) return voidGone(book, trade, gone, now, ctx);
      const mine = side(trade, intent.by)!;
      const roster = ctx.rosters[intent.by] ?? [];
      const badDrop = intent.drops.find(
        (id) => !roster.includes(id) || mine.playerIds.includes(id),
      );
      if (badDrop)
        return reject(book, 'invalid', `${ctx.playerName(badDrop)} cannot be dropped here.`);
      const withDrops: Trade = {
        ...trade,
        sides: trade.sides.map((s) =>
          s.teamId === intent.by ? { ...s, dropPlayerIds: [...intent.drops] } : s,
        ) as Trade['sides'],
      };
      const hold = holdProblem(ctx, withDrops, intent.by);
      if (hold) return reject(book, 'illegal', hold);
      const kept = keeperProblemOf(ctx, withDrops);
      if (kept) return reject(book, 'illegal', kept);
      for (const s of withDrops.sides) {
        const problem = sideProblem(ctx, withDrops, s.teamId);
        if (problem)
          return reject(
            book,
            'illegal',
            s.teamId === intent.by
              ? `Your roster after: ${problem}`
              : `${ctx.teamName(s.teamId)}'s roster after: ${problem}`,
          );
      }
      // NO REVIEW WITH A BOT ON EITHER SIDE (B-481): review is there to stop
      // two managers stacking one team at the other's cost, and a bot takes
      // no trade the read-out calls lopsided against it, so once the deal is
      // agreed it goes through, as under `review: 'none'`.
      if (ctx.rules.review === 'none' || withBot(ctx, withDrops))
        return clear(book, withDrops, now, ctx);
      const reviewEndsAt = now + ctx.rules.reviewWindowHours * H;
      const inReview: Trade = { ...withDrops, status: 'review', reviewEndsAt };
      return {
        book: replace(book, inReview),
        events: [{ type: 'review.started', trade: inReview, endsAt: reviewEndsAt }],
      };
    }
    case 'decline': {
      if (trade.status !== 'proposed')
        return reject(book, 'not-open', 'That offer is no longer open.');
      if (trade.proposerTeamId === intent.by || !side(trade, intent.by))
        return reject(book, 'not-yours', 'Only the manager who received the offer can decline it.');
      const declined = resolve(trade, 'declined', 'manager', intent.reason, now);
      return {
        book: replace(book, declined),
        events: [{ type: 'declined', trade: declined, reason: intent.reason }],
      };
    }
    case 'withdraw': {
      if (trade.status !== 'proposed')
        return reject(book, 'not-open', 'That offer is no longer open.');
      if (trade.proposerTeamId !== intent.by)
        return reject(book, 'not-yours', 'Only the manager who made the offer can take it back.');
      const withdrawn = resolve(trade, 'withdrawn', 'manager', null, now);
      return { book: replace(book, withdrawn), events: [{ type: 'withdrawn', trade: withdrawn }] };
    }
    case 'vote': {
      if (trade.status !== 'review')
        return reject(book, 'not-review', 'That trade is not under review.');
      if (ctx.rules.review !== 'league-vote')
        return reject(book, 'no-vote', 'This league does not vote on trades.');
      if (side(trade, intent.by))
        return reject(book, 'not-yours', 'You cannot vote on your own trade.');
      if (!ctx.teamIds.includes(intent.by)) return reject(book, 'unknown', 'Unknown team.');
      const votes = [
        ...trade.votes.filter((v) => v.teamId !== intent.by),
        { teamId: intent.by, veto: intent.veto, at: now },
      ];
      const voted: Trade = { ...trade, votes };
      const vetoes = votes.filter((v) => v.veto).length;
      const needed = vetoesNeeded(ctx, voted);
      const events: TradeEvent[] = [
        { type: 'voted', trade: voted, teamId: intent.by, veto: intent.veto, vetoes, needed },
      ];
      if (vetoes >= needed) {
        const vetoed = resolve(
          voted,
          'vetoed',
          'league',
          // Out of the managers who could vote: the parties never can.
          `Vetoed by league vote, ${vetoes} of ${ctx.teamIds.filter((t) => !side(voted, t)).length}.`,
          now,
        );
        events.push({
          type: 'vetoed',
          trade: vetoed,
          by: 'league',
          reason: vetoed.resolution!.reason,
        });
        return { book: replace(book, vetoed), events };
      }
      return { book: replace(book, voted), events };
    }
    case 'approve': {
      if (!isCommish)
        return reject(
          book,
          'not-commissioner',
          'Only the commissioner or a co-commissioner can approve a trade.',
        );
      if (trade.status !== 'review')
        return reject(book, 'not-review', 'That trade is not under review.');
      const r = clear(
        book,
        { ...trade, resolution: { by: 'commissioner', reason: null, at: now } },
        now,
        ctx,
      );
      return { book: r.book, events: [{ type: 'approved', trade }, ...r.events] };
    }
    case 'veto': {
      if (!isCommish)
        return reject(
          book,
          'not-commissioner',
          'Only the commissioner or a co-commissioner can veto a trade.',
        );
      if (trade.status !== 'review' && trade.status !== 'accepted')
        return reject(book, 'not-review', 'That trade is not under review.');
      if (!intent.reason.trim())
        return reject(book, 'reason', 'A veto needs a reason the league can read.');
      const vetoed = resolve(trade, 'vetoed', 'commissioner', intent.reason.trim(), now);
      return {
        book: replace(book, vetoed),
        events: [
          { type: 'vetoed', trade: vetoed, by: 'commissioner', reason: vetoed.resolution!.reason },
        ],
      };
    }
  }
}

/**
 * Move the book forward to `now`: unanswered offers expire, review windows
 * end and clear the trade, cleared trades execute when their time comes,
 * and everything open is void once the deadline passes. Hosts call this
 * on every tick or alarm; a jump of days settles everything in between.
 */
export function advance(
  book: TradeBook,
  now: number,
  ctx: TradeContext,
  /**
   * Stop after this many executions. A host whose rosters are read once per
   * pass — the league store — executes one trade, writes it, and reads
   * afresh before the next, because a second execution judged against the
   * first one's old rosters can leave a team illegal. Absent: every one.
   */
  options: { executeAtMost?: number } = {},
): Applied {
  let cur = book;
  const events: TradeEvent[] = [];
  const closed = pastDeadline(ctx);
  const limit = options.executeAtMost ?? Number.POSITIVE_INFINITY;
  for (const t of book.trades) {
    if (events.filter((e) => e.type === 'executed').length >= limit) break;
    const live = cur.trades.find((x) => x.id === t.id)!;
    if (!isOpen(live)) continue;
    if (closed && live.status === 'proposed') {
      const voided = resolve(
        live,
        'void',
        'deadline',
        'The trade deadline passed with this still open.',
        now,
      );
      cur = replace(cur, voided);
      events.push({ type: 'void', trade: voided, reason: voided.resolution!.reason! });
      continue;
    }
    if (live.status === 'proposed' && live.respondBy <= now) {
      const expired = resolve(live, 'expired', 'clock', null, now);
      cur = replace(cur, expired);
      events.push({ type: 'expired', trade: expired });
      continue;
    }
    if (live.status === 'review' && live.reviewEndsAt !== null && live.reviewEndsAt <= now) {
      const r = clear(
        cur,
        {
          ...live,
          resolution: live.resolution ?? { by: 'clock', reason: null, at: live.reviewEndsAt },
        },
        live.reviewEndsAt,
        ctx,
      );
      cur = r.book;
      events.push(...r.events);
      continue;
    }
    if (live.status === 'accepted' && live.executeAt !== null && live.executeAt <= now) {
      const r = execute(cur, live, live.executeAt, ctx);
      cur = r.book;
      events.push(...r.events);
    }
  }
  return { book: cur, events };
}

/**
 * What a side sends, named: its players, then its draft picks (§2.6 rule 8),
 * or "nothing".
 */
export function sideNames(
  s: Pick<TradeSide, 'playerIds' | 'pickIds'>,
  ctx: Pick<TradeContext, 'playerName' | 'pickName'>,
): string {
  const all = [...s.playerIds.map(ctx.playerName), ...s.pickIds.map((id) => pickNameOf(ctx, id))];
  return all.length ? all.join(', ') : 'nothing';
}

/**
 * Why a side could not seat its keepers once the trade's picks moved (§2.6
 * rule 6), or null: asked at accept, for both sides, as the proposal is.
 */
function keeperProblemOf(ctx: TradeContext, trade: Trade): string | null {
  if (ctx.keeperProblem === undefined || !trade.sides.some((s) => s.pickIds.length > 0))
    return null;
  for (const s of trade.sides) {
    const o = other(trade, s.teamId);
    const held = ctx.picks?.[s.teamId] ?? [];
    const after = [...held.filter((id) => !s.pickIds.includes(id)), ...o.pickIds];
    const problem = ctx.keeperProblem(s.teamId, after);
    if (problem) return `${ctx.teamName(s.teamId)}: ${problem}`;
  }
  return null;
}

/** One sentence for a trade's state, for the list row and the detail sheet. */
export function statusSentence(
  trade: Trade,
  ctx: Pick<TradeContext, 'teamName' | 'playerName' | 'pickName'>,
): string {
  const [a, b] = trade.sides;
  const deal = `${ctx.teamName(a.teamId)} sends ${sideNames(a, ctx)} to ${ctx.teamName(b.teamId)} for ${sideNames(b, ctx)}.`;
  switch (trade.status) {
    case 'proposed':
      return `Offered. ${deal}`;
    case 'countered':
      return `Countered. ${deal}`;
    case 'review':
      return `Accepted, under review. ${deal}`;
    case 'accepted':
      return `Cleared; goes through after the week's games. ${deal}`;
    case 'executed':
      return `Done. ${deal}`;
    case 'vetoed':
      return trade.resolution?.by === 'commissioner'
        ? `Vetoed by the commissioner: "${trade.resolution.reason ?? ''}"`
        : (trade.resolution?.reason ?? 'Vetoed by league vote.');
    case 'declined':
      return trade.resolution?.reason ? `Declined: "${trade.resolution.reason}"` : 'Declined.';
    case 'withdrawn':
      return trade.resolution?.reason ?? 'Offer withdrawn.';
    case 'expired':
      return `${ctx.teamName(b.teamId)} never answered. Offer expired.`;
    case 'void':
      return trade.resolution?.reason ?? 'Void.';
  }
}
